import "./inGameMenu.scss";
import { useEffect, useRef } from "react";
import { useGame } from "../../../context/GameContext";
import { FaCheckCircle, FaCircle } from "react-icons/fa";
import VolumeSlider from "./components/Volume";
import { handleHome, restartGame } from "../../../utils/gameControls";
import { eventQuitGame } from "../../../utils/gtag";
import useSoundEffect from "../../../hooks/useSoundEffect";
import { KeyCodes } from "../../../utils/navigationHelpers";
import ChessButton from "../../visual/ChessButton/ChessButton";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import { DIFFICULTY } from "../../../utils/constants";
import { Difficulty } from "../../../types/general";
import { useDevice } from "../../../context/DeviceContext";

/**
 * Creates the in-game menu icon to show when the gear icon is selected.
 */
export default function InGameMenu() {
    const { gameState, setGameState, setRemoteNavigation } = useGame();
    const { setMultiplayerState } = useMultiplayer();
    const { isTV: isUserOnTV, isMobile: isUserOnMobile } = useDevice();

    const { multiplayerState, forfeitGame } = useMultiplayer();

    const { playClickAudio, playSelectAudio } = useSoundEffect();
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                KeyCodes.Ups.includes(event.key) ||
                KeyCodes.Downs.includes(event.key) ||
                KeyCodes.Lefts.includes(event.key) ||
                KeyCodes.Rights.includes(event.key)
            ) {
                playClickAudio();
            }
        };

        if (gameState.showInGameMenu) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [gameState.showInGameMenu, playClickAudio]);

    if (!gameState.game) {
        console.error("Game is null");
        return null;
    }
    if (!gameState.showInGameMenu) return null;

    const showForfeitButton =
        !gameState.isAIGame &&
        !multiplayerState.isHost &&
        !gameState.game.isGameOver() &&
        !multiplayerState.forfeit.isForfeit &&
        !gameState.isPuzzle;

    const showHomeButton = gameState.isAIGame || multiplayerState.isHost || gameState.isPuzzle;
    const showRestartButton = gameState.isAIGame;

    const toggleMenu = () => {
        setGameState((prev) => {
            return {
                ...prev,
                showInGameMenu: !prev.showInGameMenu,
            };
        });
    };

    const onRestartGameClick = () => {
        toggleMenu();
        restartGame(setGameState);
    };

    const onHomeClick = () => {
        toggleMenu();
        handleHome(setGameState, setRemoteNavigation, setMultiplayerState);
    };

    const onDifficultyClick = (newDifficulty: string) => {
        setGameState((prev) => ({ ...prev, aiDifficulty: newDifficulty as Difficulty }));
    };

    const onForfeit = () => {
        toggleMenu();
        forfeitGame();
    };

    // Named row indices for in-game menu navigation.
    const ROW_DIFFICULTY = 0;
    const ROW_RETURN = 1;
    const ROW_RESTART = 2;
    const ROW_FORFEIT = showRestartButton ? 3 : 2;
    const ROW_HOME = showRestartButton ? (showForfeitButton ? 4 : 3) : 2;
    const ROW_VOLUME = showRestartButton ? (showForfeitButton ? 5 : 4) : 3;

    return (
        <div className="overlay">
            <div id="in-game-menu" className="chess-menu" ref={menuRef}>
                <button
                    className="close-menu-button"
                    onClick={() => {
                        playSelectAudio();
                        toggleMenu();
                    }}
                    aria-label="Close Menu"
                >
                    <img src="/images/close-circle-gold.webp" alt="" className="close-menu-icon" />
                </button>

                <h1>Options</h1>
                {gameState.isAIGame && (
                    <div id="difficulty">
                        <h2>Difficulty</h2>
                        <div id="difficulty-options">
                            {Object.keys(DIFFICULTY.OPTIONS).map((difficulty, idx) => {
                                const isSelected = difficulty === gameState.aiDifficulty;
                                return (
                                    <button
                                        data-navrow={ROW_DIFFICULTY}
                                        data-navcol={idx}
                                        data-testid={`difficulty-${difficulty}`}
                                        key={idx}
                                        className="chess-button"
                                        onClick={() => {
                                            onDifficultyClick(difficulty);
                                            playSelectAudio();
                                        }}
                                        id={isSelected ? "selected-difficulty" : ""}
                                    >
                                        {difficulty}
                                        <span className="icon-container">
                                            {isSelected ? (
                                                <FaCheckCircle size={25} />
                                            ) : (
                                                <FaCircle size={25} />
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                <ChessButton
                    onClick={toggleMenu}
                    data-navcol={0}
                    data-navrow={ROW_RETURN}
                    autoFocus={isUserOnTV}
                >
                    Return To Game
                </ChessButton>
                {showRestartButton && (
                    <ChessButton
                        data-navcol={0}
                        data-navrow={ROW_RESTART}
                        data-testid="restart"
                        onClick={onRestartGameClick}
                    >
                        Reset Board
                    </ChessButton>
                )}
                {showForfeitButton && (
                    <ChessButton
                        data-testid="cancel"
                        data-navcol={0}
                        data-navrow={ROW_FORFEIT}
                        onClick={() => {
                            onForfeit();
                            eventQuitGame();
                        }}
                    >
                        Forfeit Game
                    </ChessButton>
                )}
                {showHomeButton && (
                    <ChessButton
                        data-navcol={0}
                        data-navrow={ROW_HOME}
                        data-testid="home"
                        onClick={() => {
                            onHomeClick();
                            eventQuitGame();
                        }}
                    >
                        Main Menu
                    </ChessButton>
                )}
                {!isUserOnMobile && <VolumeSlider navRow={ROW_VOLUME} navCol={0} />}
            </div>
        </div>
    );
}
